<template>
  <div class="case-page">
    <PageHeroBanner title="案例展示" title-en="CASE SHOW" :image="bannerBg" />

    <div class="container page-sub">
      <p class="breadcrumb">
        <router-link to="/">首页</router-link>
        <span class="breadcrumb__sep">/</span>
        <span class="breadcrumb__here">案例展示</span>
      </p>
      <p class="page-lead">持续拥抱物联网与人工智能浪潮，助力文化行业创新发展</p>
    </div>

    <div class="container page-layout">
      <aside class="side-nav" aria-label="案例分类">
        <button
          v-for="cat in categories"
          :key="cat.id"
          type="button"
          class="side-nav__btn"
          :class="{ active: String(cat.id) === String(activeCategoryId) }"
          @click="selectCategory(cat.id)"
        >
          <span class="side-nav__name">{{ cat.name || cat.title || '-' }}</span>
        </button>
      </aside>

      <div class="main">
        <div v-if="loading" class="state state--loading">加载中…</div>
        <div v-else-if="!caseList.length" class="state">暂无案例数据</div>
        <div v-else class="grid">
          <article v-for="(item, idx) in caseList" :key="item.id" class="tile">
            <router-link :to="detailRoute(item)" class="tile-link">
              <div class="tile-media" :class="{ 'tile-media--ready': imageReady[item.id] }">
                <img
                  v-if="item.image"
                  class="tile-img"
                  :src="item.image"
                  :alt="item.title"
                  :loading="idx < 8 ? 'eager' : 'lazy'"
                  decoding="async"
                  :fetchpriority="idx === 0 ? 'high' : idx < 8 ? 'auto' : 'low'"
                  @load="onTileImageLoad(item.id)"
                  @error="onTileImageLoad(item.id)"
                >
                <div v-else class="tile-ph">{{ shortTitle(item.title) }}</div>
              </div>
              <div class="tile-meta">
                <h2 class="tile-title">{{ item.title }}</h2>
                <p v-if="item.subtitle" class="tile-sub">{{ item.subtitle }}</p>
                <span class="tile-more">了解更多 &gt;&gt;</span>
              </div>
            </router-link>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchCaseCategories, fetchCasesByCategory } from '../api/cases'
import PageHeroBanner from '../components/PageHeroBanner.vue'
import { PAGE_BANNER_BG } from '../constants/pageBannerImages'

export default {
  name: 'CaseCenterView',
  components: { PageHeroBanner },
  data() {
    return {
      bannerBg: PAGE_BANNER_BG.cases,
      categories: [],
      activeCategoryId: '',
      caseList: [],
      loading: false,
      imageReady: {}
    }
  },
  watch: {
    '$route.query.category': {
      immediate: true,
      handler(category) {
        if (category && this.categories.length) {
          this.activeCategoryId = String(category)
          this.loadCases(String(category))
        }
      }
    }
  },
  async mounted() {
    this.loading = true
    try {
      const list = await fetchCaseCategories()
      this.categories = Array.isArray(list) ? list : []
      const fromQuery = this.$route.query.category
      const firstId = this.categories[0] ? String(this.categories[0].id) : ''
      this.activeCategoryId = String(fromQuery || firstId)
      if (this.activeCategoryId) {
        await this.loadCases(this.activeCategoryId)
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    async loadCases(categoryId) {
      this.loading = true
      this.imageReady = {}
      try {
        const list = await fetchCasesByCategory(categoryId)
        this.caseList = Array.isArray(list) ? list : []
      } finally {
        this.loading = false
      }
    },
    /** 左侧分类 id → GET /api/v1/anlis（带 anli_category_id / category_id）刷新右侧列表 */
    selectCategory(id) {
      const cid = String(id)
      if (cid === String(this.activeCategoryId)) return
      this.activeCategoryId = cid
      this.$router.replace({ path: '/cases', query: { category: cid } })
    },
    detailRoute(item) {
      return {
        name: 'case-detail',
        params: { id: String(item.id) },
        query: { category: String(this.activeCategoryId || '') }
      }
    },
    shortTitle(t) {
      if (!t || typeof t !== 'string') return '-'
      return t.length <= 6 ? t : t.slice(0, 4)
    },
    onTileImageLoad(id) {
      if (id == null) return
      this.imageReady = { ...this.imageReady, [id]: true }
    }
  }
}
</script>

<style scoped>
.case-page {
  padding-top: 0;
  min-height: 100vh;
  background: #f5f9ff;
}

.page-sub { padding: 20px 20px 8px; }
.breadcrumb { font-size: 14px; color: #64748b; margin: 0 0 8px; }
.breadcrumb a { color: #2563eb; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb__sep { margin: 0 8px; color: #cbd5e1; }
.breadcrumb__here { color: #334155; }
.page-lead { font-size: 15px; color: #64748b; margin: 0 0 20px; }

.page-layout { display: grid; grid-template-columns: 220px 1fr; gap: 22px; align-items: start; }
.main { min-height: 320px; }
.side-nav { background: #fff; border: 1px solid #dbe7f5; border-radius: 10px; padding: 10px 0; box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04); }
.side-nav__btn { width: 100%; border: none; background: transparent; cursor: pointer; text-align: left; padding: 14px 16px; border-left: 3px solid transparent; }
.side-nav__btn:hover { background: #f8fbff; }
.side-nav__btn.active { background: #edf5ff; border-left-color: #2563eb; }
.side-nav__name { font-size: 15px; color: #0f172a; font-weight: 600; }

.grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; padding-bottom: 80px; }
.tile { background: #fff; border: 1px solid #dbe7f5; border-radius: 10px; overflow: hidden; transition: box-shadow 0.25s, transform 0.25s; }
.tile:hover { box-shadow: 0 16px 40px rgba(37, 99, 235, 0.12); transform: translateY(-4px); }
.tile-link { display: block; color: inherit; }
.tile-media { aspect-ratio: 4 / 3; background: #f1f5f9; overflow: hidden; position: relative; }
.tile-media::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.55) 45%, transparent 90%);
  background-size: 220% 100%;
  animation: tile-media-shimmer 1.35s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
}
.tile-media--ready::after { display: none; }
.tile-img {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.35s ease;
}
.tile-media--ready .tile-img { opacity: 1; }
@keyframes tile-media-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.tile-ph { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%); color: #1e3a8a; font-size: 26px; font-weight: 700; }
.tile-meta { padding: 16px 16px 20px; }
.tile-title { font-size: 18px; color: #0f172a; font-weight: 600; margin: 0 0 6px; }
.tile-sub { font-size: 14px; color: #64748b; line-height: 1.55; margin: 0 0 10px; }
.tile-more { font-size: 13px; color: #2563eb; font-weight: 500; }

.state { padding: 48px; text-align: center; color: #64748b; background: #fff; border-radius: 10px; border: 1px dashed #cbd5e1; margin-bottom: 80px; }
.state--loading { color: #2563eb; }

@media (max-width: 1024px) { .grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) {
  .page-layout { grid-template-columns: 1fr; }
  .side-nav { display: flex; flex-wrap: wrap; gap: 8px; padding: 12px; }
  .side-nav__btn { width: auto; border-left: none; border: 1px solid #dbe7f5; border-radius: 8px; padding: 10px 12px; }
  .side-nav__btn.active { border-color: #2563eb; }
  .grid { grid-template-columns: 1fr; }
}
</style>
